import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import type { IntlShape } from 'react-intl';
import { PiX } from 'react-icons/pi';
import { ICON_BUTTON_SIZE_SM, IconButton } from '@aibuild/ui';
import { formatLength, formatQuantity, formatSize } from './format';
import type { ModelSize } from './format';
import type { MeasureState, SelectedPart } from './viewport';
import {
  PanelCard,
  PanelHeading,
  PanelHint,
  PanelLabel,
  PanelRows,
  PanelTitle,
  PanelValue,
} from './ViewportPanels.styled';

const messages = defineMessages({
  close: { id: 'modelViewer.panel.close', defaultMessage: 'Close' },
  measure: { id: 'modelViewer.measure', defaultMessage: 'Measure' },
  hintFirst: { id: 'modelViewer.measure.hintFirst', defaultMessage: 'Click a point on the part' },
  hintSecond: { id: 'modelViewer.measure.hintSecond', defaultMessage: 'Click a second point' },
  distance: { id: 'modelViewer.measure.distance', defaultMessage: 'Distance' },
  delta: { id: 'modelViewer.measure.delta', defaultMessage: 'Δ{axis}' },
  part: { id: 'modelViewer.part', defaultMessage: 'Part {number}' },
  size: { id: 'modelViewer.part.size', defaultMessage: 'Size' },
  surfaceArea: { id: 'modelViewer.part.surfaceArea', defaultMessage: 'Surface area' },
  volume: { id: 'modelViewer.part.volume', defaultMessage: 'Volume' },
  model: { id: 'modelViewer.model', defaultMessage: 'Model' },
  tooDense: {
    id: 'modelViewer.model.tooDense',
    defaultMessage: 'Too detailed to select parts or measure',
  },
});

const HINTS = [messages.hintFirst, messages.hintSecond];

interface Row {
  label: string;
  value: string;
}

function sizeRow(intl: IntlShape, size: ModelSize): Row {
  return { label: intl.formatMessage(messages.size), value: formatSize(intl, size) };
}

interface InfoPanelProps {
  title: string;
  rows?: Row[];
  hint?: string;
  testId: string;
  onClose?: () => void;
}

const InfoPanel: React.FC<InfoPanelProps> = ({ title, rows = [], hint, testId, onClose }) => {
  const intl = useIntl();
  return (
    <PanelCard aria-label={title} data-testid={testId}>
      <PanelHeading>
        <PanelTitle>{title}</PanelTitle>
        {onClose && (
          <IconButton
            ariaLabel={intl.formatMessage(messages.close)}
            size={ICON_BUTTON_SIZE_SM}
            icon={<PiX />}
            onClick={onClose}
            dataTestId={`${testId}__close`}
          />
        )}
      </PanelHeading>
      {hint && <PanelHint>{hint}</PanelHint>}
      {rows.length > 0 && (
        <PanelRows>
          {rows.map(({ label, value }) => (
            <React.Fragment key={label}>
              <PanelLabel>{label}</PanelLabel>
              <PanelValue>{value}</PanelValue>
            </React.Fragment>
          ))}
        </PanelRows>
      )}
    </PanelCard>
  );
};

interface MeasurePanelProps {
  measure: MeasureState;
  onClose: () => void;
}

const MeasurePanel: React.FC<MeasurePanelProps> = ({ measure, onClose }) => {
  const intl = useIntl();
  const { measurement, placed } = measure;
  const millimetres = (value: number) => `${formatLength(intl, value)} mm`;
  const delta = (axis: 'x' | 'y' | 'z') => ({
    label: intl.formatMessage(messages.delta, { axis: axis.toUpperCase() }),
    value: millimetres(measurement?.delta[axis] ?? 0),
  });
  const rows = measurement
    ? [
        {
          label: intl.formatMessage(messages.distance),
          value: millimetres(measurement.distance),
        },
        delta('x'),
        delta('y'),
        delta('z'),
      ]
    : undefined;
  const hint = measurement
    ? undefined
    : intl.formatMessage(HINTS[Math.min(placed, HINTS.length - 1)]);
  return (
    <InfoPanel
      testId="model-viewer__measure-panel"
      title={intl.formatMessage(messages.measure)}
      hint={hint}
      rows={rows}
      onClose={onClose}
    />
  );
};

interface PartPanelProps {
  part: SelectedPart;
  onClose: () => void;
}

const PartPanel: React.FC<PartPanelProps> = ({ part, onClose }) => {
  const intl = useIntl();
  const number = (value: number) => formatQuantity(intl, value);
  const { size, surfaceArea, volume } = part.metrics;
  const rows: Row[] = [
    sizeRow(intl, size),
    { label: intl.formatMessage(messages.surfaceArea), value: `${number(surfaceArea)} mm²` },
  ];
  if (volume !== null) {
    rows.push({ label: intl.formatMessage(messages.volume), value: `${number(volume)} mm³` });
  }
  return (
    <InfoPanel
      testId="model-viewer__part-panel"
      title={intl.formatMessage(messages.part, { number: part.index + 1 })}
      rows={rows}
      onClose={onClose}
    />
  );
};

const ModelPanel: React.FC<{ size: ModelSize }> = ({ size }) => {
  const intl = useIntl();
  return (
    <InfoPanel
      testId="model-viewer__model-panel"
      title={intl.formatMessage(messages.model)}
      rows={[sizeRow(intl, size)]}
      hint={intl.formatMessage(messages.tooDense)}
    />
  );
};

export interface ViewportPanelsProps {
  part: SelectedPart | null;
  measuring: boolean;
  measure: MeasureState;
  /** Shown in place of a part's metrics when the model is too dense to pick. */
  modelSize?: ModelSize | null;
  onClosePart: () => void;
  onCloseMeasure: () => void;
}

const ViewportPanels: React.FC<ViewportPanelsProps> = ({
  part,
  measuring,
  measure,
  modelSize,
  onClosePart,
  onCloseMeasure,
}) => {
  if (measuring) return <MeasurePanel measure={measure} onClose={onCloseMeasure} />;
  if (part) return <PartPanel part={part} onClose={onClosePart} />;
  return modelSize ? <ModelPanel size={modelSize} /> : null;
};

export default ViewportPanels;
